import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { ToastContext } from "context/ToastContext";
import { centsToDollars, parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTablePFSEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import {
  useGetAthenaPostingsQuery,
  useGetAthenaPostingStatusQuery,
  useRetryAthenaPostingMutation,
} from "store/queries/athenaPostings";
import {
  AthenaPostingStatus,
  IAthenaPostingLogItem,
} from "types/AthenaIntegration";

import { apiErrorMessage, formatDateTime } from "../../utils/hstImports";

const PAGE_SIZE = 25;

// In the cron's own order: queued → posted, or failed (retried with backoff) → skipped (given up on).
const STATUSES: AthenaPostingStatus[] = [
  "PENDING",
  "POSTED",
  "FAILED",
  "SKIPPED",
];

const STATUS_SEVERITY: Record<
  AthenaPostingStatus,
  "success" | "danger" | "warning" | "info"
> = {
  PENDING: "info",
  POSTED: "success",
  FAILED: "danger",
  SKIPPED: "warning",
};

const STATUS_LABEL: Record<AthenaPostingStatus, string> = {
  PENDING: "Pending",
  POSTED: "Posted",
  FAILED: "Failed",
  SKIPPED: "Skipped",
};

const STATUS_FILTER_OPTIONS = [
  { label: "All statuses", value: undefined },
  ...STATUSES.map((s) => ({ label: STATUS_LABEL[s], value: s })),
];

const statusBody = (p: IAthenaPostingLogItem) => (
  <Tag value={STATUS_LABEL[p.status]} severity={STATUS_SEVERITY[p.status]} />
);

const orderBody = (p: IAthenaPostingLogItem) => (
  <Link to={`/orders/${p.orderId}`}>{p.orderId}</Link>
);

const amountBody = (p: IAthenaPostingLogItem) =>
  parsePrice(centsToDollars(p.amountInCents));

const lastAttemptBody = (p: IAthenaPostingLogItem) =>
  formatDateTime(p.lastAttemptAt);

const nextRetryBody = (p: IAthenaPostingLogItem) =>
  formatDateTime(p.nextRetryAt);

const errorBody = (p: IAthenaPostingLogItem) =>
  p.errorMessage ? (
    <span className="text-red-600" style={{ wordBreak: "break-word" }}>
      {p.errorMessage}
    </span>
  ) : null;

function StatusCards() {
  const { data, isFetching } = useGetAthenaPostingStatusQuery();
  const counts = data?.statusCounts || {};
  return (
    <div className="flex flex-column gap-2">
      <div className="grid">
        {STATUSES.map((status) => (
          <div key={status} className="col-6 md:col-3">
            <div
              className="surface-ground border-round p-3 text-center"
              data-testid={`athena-posting-count-${status}`}
            >
              <div className="text-2xl font-bold">
                {isFetching && !data ? "…" : counts[status] ?? 0}
              </div>
              <div className="text-600 text-sm">{STATUS_LABEL[status]}</div>
            </div>
          </div>
        ))}
      </div>
      <small className="text-600">Postings created in the last 24 hours.</small>
    </div>
  );
}

export function AthenaPostingsSection() {
  const toastRef = useContext(ToastContext);
  const [status, setStatus] = useState<AthenaPostingStatus | undefined>(
    undefined
  );
  const [page, setPage] = useState(1);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const { data, isFetching } = useGetAthenaPostingsQuery({
    status,
    page,
    pageSize: PAGE_SIZE,
  });
  const [retryPosting] = useRetryAthenaPostingMutation();
  const list = data?.data;
  const items = list?.items || [];

  const onPage = (event: DataTablePFSEvent) => {
    setPage(Math.floor(event.first / event.rows) + 1);
  };

  const onRetry = async (posting: IAthenaPostingLogItem) => {
    setRetryingId(posting.id);
    try {
      await retryPosting(posting.id).unwrap();
      toastRef?.current?.show({
        severity: "success",
        summary: "Retry queued",
        detail:
          "The posting will be sent to Athena on the next run of the posting job.",
      });
    } catch (error) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Could not retry",
        detail: apiErrorMessage(error).message,
      });
    } finally {
      setRetryingId(null);
    }
  };

  const actionsBody = (p: IAthenaPostingLogItem) =>
    p.status === "FAILED" ? (
      <Button
        label="Retry now"
        icon="pi pi-refresh"
        className="p-button-text p-button-sm"
        loading={retryingId === p.id}
        disabled={retryingId !== null}
        onClick={() => onRetry(p)}
        data-testid={`athena-posting-retry-${p.id}`}
      />
    ) : null;

  return (
    <div className="flex flex-column gap-3">
      <StatusCards />
      <div className="flex flex-wrap align-items-center justify-content-between gap-3">
        <Dropdown
          value={status}
          options={STATUS_FILTER_OPTIONS}
          onChange={(e) => {
            setStatus(e.value || undefined);
            setPage(1);
          }}
          placeholder="All statuses"
          style={{ minWidth: "200px" }}
          aria-label="Filter postings by status"
        />
        <small className="text-600">
          Failed postings first. Retry now sends a failed posting on the next
          run of the posting job.
        </small>
      </div>

      <PageDataTable
        value={items}
        dataKey="id"
        lazy
        paginator
        first={(page - 1) * PAGE_SIZE}
        rows={PAGE_SIZE}
        totalRecords={list?.total || 0}
        onPage={onPage}
        loading={isFetching}
        emptyMessage="No Athena payment postings."
      >
        <Column header="Order" body={orderBody} />
        <Column header="Provider" field="providerName" />
        <Column header="Account" field="accountName" />
        <Column header="Amount" body={amountBody} />
        <Column header="Status" body={statusBody} />
        <Column header="Attempts" field="attempts" />
        <Column header="Last attempt" body={lastAttemptBody} />
        <Column header="Next retry" body={nextRetryBody} />
        <Column header="Error" body={errorBody} />
        <Column header="" body={actionsBody} style={{ width: "9rem" }} />
      </PageDataTable>
    </div>
  );
}
